// bluaIQ — Receipts Module
// Scan receipts with AI, track project expenses
// Auto-catalogs all expenses into a summary sheet

import { apiFetch } from '../utils/api'
import { friendlyApiError, isPlanGateStatus } from '../utils/apiErrors'
import { formatDateOnly } from '../utils/dateOnly'
import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Upload, Receipt, Loader2, DollarSign, Calendar, ShoppingBag,
  CreditCard, TrendingUp, ArrowUpDown, Download, LayoutList, BarChart3, Trash2, Eye
} from 'lucide-react'
import ProFeatureGate, { ProBadge } from './ProFeatureGate'
import { isDemoProject } from '../utils/demoData'
import SavedProjectOnlyNotice from './SavedProjectOnlyNotice'
import { downloadCsv } from '../utils/exportCsv'
import { useSubscriptionPlan } from '../hooks/useSubscriptionPlan'

// ── TYPES ─────────────────────────────────────────

interface ReceiptItem {
  name: string
  quantity: number
  unitPrice: number
  total: number
  category?: string
}

interface ReceiptData {
  id: string
  store?: string
  date?: string
  subtotal?: number
  tax?: number
  total?: number
  paymentMethod?: string
  items?: ReceiptItem[]
  createdAt: string
  // Source-file preservation (Phase 2a). When present, the receipt card can
  // open the ORIGINAL uploaded PDF/photo for review against the AI extraction.
  storagePath?: string | null
  originalFilename?: string | null
}

// PR-NEW: numeric coercion helpers. The AI scan route persists
// item records whose numeric fields may be missing, null, or
// strings (legacy rows stored the raw AI shape with field names
// `description` / `totalPrice`). Calling `.toFixed` on undefined
// throws and bubbles to the global ErrorBoundary — exactly the
// production /receipts crash. `safeNumber` coerces any value to
// a finite number with a 0 fallback; `safeMoney` produces a
// fixed-precision string for `$X.XX` display.
function safeNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = parseFloat(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return fallback
}
function safeMoney(value: unknown): string {
  return safeNumber(value).toFixed(2)
}

// PR-NEW: normalize a receipt's `items` array so the FE can
// render existing rows that were persisted in the raw AI shape
// (`description` / `totalPrice`) AND new rows persisted in the
// FE shape (`name` / `total`). Defensive against non-array items
// columns and malformed entries.
function normalizeReceiptItem(raw: any): ReceiptItem {
  if (!raw || typeof raw !== 'object') {
    return { name: '', quantity: 0, unitPrice: 0, total: 0, category: 'OTHER' }
  }
  const name = typeof raw.name === 'string' && raw.name
    ? raw.name
    : (typeof raw.description === 'string' ? raw.description : '')
  const total = typeof raw.total === 'number'
    ? raw.total
    : (typeof raw.totalPrice === 'number' ? raw.totalPrice : safeNumber(raw.totalPrice))
  return {
    name,
    quantity: safeNumber(raw.quantity),
    unitPrice: safeNumber(raw.unitPrice),
    total: safeNumber(total),
    category: typeof raw.category === 'string' ? raw.category : 'OTHER',
  }
}
function normalizeReceipt(raw: any): ReceiptData {
  const rawItems = Array.isArray(raw?.items) ? raw.items : []
  return {
    ...raw,
    items: rawItems.map(normalizeReceiptItem),
  }
}

// PR-NEW: payment-method parser. The AI scan stores
// `paymentMethod` as a free-form string ("Visa ****1234",
// "Account #HD-88912", "Check #1087", "Cash", null, …). We
// derive a stable group key + a masked display label without
// ever surfacing more than the last 4 of any card. Schema
// stays as a single String column — no migration.
//
//   key     — stable identifier for grouping/filtering
//             ("visa:4521", "account:88912", "check", "cash",
//             "unknown")
//   brand   — display brand prefix ("Visa", "Mastercard",
//             "Amex", "Discover", "EFT/Debit", "Account",
//             "Check", "Cash", "Unknown")
//   last4   — last 4 digits if extractable, else null
//   label   — bilingual-aware compact display
//             ("Visa •••• 4521", "Cash", "Unknown")
export interface PaymentMethodInfo {
  key: string
  brand: string
  last4: string | null
  label: string
}

const PAYMENT_BRAND_PATTERNS: Array<{ re: RegExp; brand: string; keyPrefix: string }> = [
  { re: /\bvisa\b/i,                 brand: 'Visa',         keyPrefix: 'visa' },
  { re: /\bmaster\s*card\b|\bmc\b/i, brand: 'Mastercard',   keyPrefix: 'mastercard' },
  { re: /\b(amex|american\s+express)\b/i, brand: 'Amex',    keyPrefix: 'amex' },
  { re: /\bdiscover\b/i,             brand: 'Discover',     keyPrefix: 'discover' },
  { re: /\beft\b|\bdebit\b/i,        brand: 'EFT/Debit',    keyPrefix: 'debit' },
  { re: /\bcheck\b|\bcheque\b/i,     brand: 'Check',        keyPrefix: 'check' },
  { re: /\bcash\b|\befectivo\b/i,    brand: 'Cash',         keyPrefix: 'cash' },
  { re: /\baccount\b|\bcuenta\b|\bhouse\b/i, brand: 'Account', keyPrefix: 'account' },
]

export function parsePaymentMethod(raw: string | null | undefined, isEs = false): PaymentMethodInfo {
  const trimmed = typeof raw === 'string' ? raw.trim() : ''
  if (!trimmed) {
    const unknown = isEs ? 'Desconocido' : 'Unknown'
    return { key: 'unknown', brand: unknown, last4: null, label: unknown }
  }
  // Try to lift the last 4 digits — common formats are
  // "**** 1234", "****1234", "•••• 1234", "ending in 1234",
  // "#1234". We take the LAST run of 4 consecutive digits so
  // an account number prefix doesn't get confused for the last 4.
  const digitGroups = trimmed.match(/\d{4,}/g) || []
  let last4: string | null = null
  if (digitGroups.length > 0) {
    const lastGroup = digitGroups[digitGroups.length - 1]
    last4 = lastGroup.slice(-4)
  }

  let brand = ''
  let keyPrefix = ''
  for (const p of PAYMENT_BRAND_PATTERNS) {
    if (p.re.test(trimmed)) {
      brand = p.brand
      keyPrefix = p.keyPrefix
      break
    }
  }
  // Cash / Check / Account variants with no number → no last4
  // displayed. Cash specifically should NOT show a "•••• ____"
  // when the receipt text happens to contain numbers.
  const isNumberlessBrand = keyPrefix === 'cash'
  if (isNumberlessBrand) last4 = null

  // If no brand matched, fall back to a generic "Card" /
  // "Tarjeta" prefix when we have a last 4, else "Other" /
  // "Otro".
  if (!brand) {
    if (last4) {
      brand = isEs ? 'Tarjeta' : 'Card'
      keyPrefix = 'card'
    } else {
      brand = isEs ? 'Otro' : 'Other'
      keyPrefix = 'other'
    }
  }

  const localizedBrand = (() => {
    if (!isEs) return brand
    switch (brand) {
      case 'Check': return 'Cheque'
      case 'Cash': return 'Efectivo'
      case 'Account': return 'Cuenta'
      default: return brand
    }
  })()

  const key = last4 ? `${keyPrefix}:${last4}` : keyPrefix
  const label = last4 ? `${localizedBrand} •••• ${last4}` : localizedBrand
  return { key, brand: localizedBrand, last4, label }
}

// ── DEMO DATA ─────────────────────────────────────

const DEMO_RECEIPTS: ReceiptData[] = [
  {
    id: 'r1', store: '84 Lumber — Garland', date: '2026-03-18', subtotal: 2847.50, tax: 234.92, total: 3082.42, paymentMethod: 'Visa ****4521',
    items: [
      { name: '2x4x8 KD SPF Stud', quantity: 180, unitPrice: 4.29, total: 772.20, category: 'FRAMING' },
      { name: '2x6x16 SPF', quantity: 45, unitPrice: 9.89, total: 445.05, category: 'FRAMING' },
      { name: 'OSB 7/16 4x8 Sheathing', quantity: 52, unitPrice: 14.99, total: 779.48, category: 'FRAMING' },
      { name: 'Simpson LTP4 Tie Plates', quantity: 24, unitPrice: 3.49, total: 83.76, category: 'HARDWARE' },
      { name: '16d Framing Nails 5lb box', quantity: 6, unitPrice: 12.99, total: 77.94, category: 'HARDWARE' },
      { name: 'LVL 1.75x11.25x20 Beam', quantity: 2, unitPrice: 344.54, total: 689.07, category: 'FRAMING' },
    ],
    createdAt: '2026-03-18T14:30:00Z',
  },
  {
    id: 'r2', store: 'Home Depot Pro — Plano', date: '2026-03-15', subtotal: 1456.80, tax: 120.19, total: 1576.99, paymentMethod: 'Account #HD-88912',
    items: [
      { name: 'R-38 Fiberglass Batt 24" (8-pack)', quantity: 12, unitPrice: 54.99, total: 659.88, category: 'INSULATION' },
      { name: 'R-13 Wall Insulation 15" (12-pack)', quantity: 8, unitPrice: 42.99, total: 343.92, category: 'INSULATION' },
      { name: 'Spray Foam Sealant (12 oz)', quantity: 6, unitPrice: 7.49, total: 44.94, category: 'INSULATION' },
      { name: 'Vapor Barrier 10x100 6mil', quantity: 2, unitPrice: 89.99, total: 179.98, category: 'INSULATION' },
      { name: 'Insulation Supports 24"', quantity: 4, unitPrice: 57.02, total: 228.08, category: 'INSULATION' },
    ],
    createdAt: '2026-03-15T10:15:00Z',
  },
  {
    id: 'r3', store: 'Beacon Roofing — Irving', date: '2026-03-12', subtotal: 4210.00, tax: 347.33, total: 4557.33, paymentMethod: 'Check #1087',
    items: [
      { name: 'OC Duration Shingles (Onyx Black) per sq', quantity: 22, unitPrice: 112.50, total: 2475.00, category: 'ROOFING' },
      { name: 'Synthetic Underlayment 10sq roll', quantity: 3, unitPrice: 159.99, total: 479.97, category: 'ROOFING' },
      { name: 'Drip Edge 10ft galv', quantity: 24, unitPrice: 8.99, total: 215.76, category: 'ROOFING' },
      { name: 'Ice & Water Shield 2sq', quantity: 2, unitPrice: 124.99, total: 249.98, category: 'ROOFING' },
      { name: 'Ridge Vent 4ft', quantity: 12, unitPrice: 18.49, total: 221.88, category: 'ROOFING' },
      { name: 'Coil Roofing Nails 1.25"', quantity: 4, unitPrice: 66.85, total: 267.41, category: 'ROOFING' },
      { name: 'Starter Strip Shingles', quantity: 6, unitPrice: 50.00, total: 300.00, category: 'ROOFING' },
    ],
    createdAt: '2026-03-12T09:00:00Z',
  },
  {
    id: 'r4', store: 'Ferguson — Dallas', date: '2026-03-08', subtotal: 890.00, tax: 73.43, total: 963.43, paymentMethod: 'Visa ****4521',
    items: [
      { name: 'PEX 3/4" x 100ft Red', quantity: 2, unitPrice: 89.99, total: 179.98, category: 'PLUMBING' },
      { name: 'PEX 3/4" x 100ft Blue', quantity: 2, unitPrice: 89.99, total: 179.98, category: 'PLUMBING' },
      { name: 'PEX 1/2" x 100ft Red', quantity: 2, unitPrice: 54.99, total: 109.98, category: 'PLUMBING' },
      { name: 'SharkBite Fittings Assorted', quantity: 1, unitPrice: 124.99, total: 124.99, category: 'PLUMBING' },
      { name: 'Water Heater 50gal Gas', quantity: 1, unitPrice: 295.07, total: 295.07, category: 'PLUMBING' },
    ],
    createdAt: '2026-03-08T11:20:00Z',
  },
  {
    id: 'r5', store: 'Lowe\'s Pro — McKinney', date: '2026-03-05', subtotal: 534.20, tax: 44.07, total: 578.27, paymentMethod: 'Visa ****4521',
    items: [
      { name: 'Concrete Mix 80lb bags', quantity: 15, unitPrice: 6.49, total: 97.35, category: 'FOUNDATION' },
      { name: 'Rebar #4 20ft', quantity: 12, unitPrice: 14.99, total: 179.88, category: 'FOUNDATION' },
      { name: 'Wire Mesh 5x150 roll', quantity: 1, unitPrice: 189.99, total: 189.99, category: 'FOUNDATION' },
      { name: 'Form Stakes 24" (50pk)', quantity: 2, unitPrice: 33.49, total: 66.98, category: 'FOUNDATION' },
    ],
    createdAt: '2026-03-05T08:45:00Z',
  },
  {
    id: 'r6', store: 'Floor & Decor — Dallas', date: '2026-03-20', subtotal: 3245.00, tax: 267.71, total: 3512.71, paymentMethod: 'Visa ****4521',
    items: [
      { name: 'Vanity 48" Double Sink White', quantity: 2, unitPrice: 489.99, total: 979.98, category: 'DECOR' },
      { name: 'Frameless Mirror 36x48"', quantity: 2, unitPrice: 179.99, total: 359.98, category: 'DECOR' },
      { name: 'Vessel Faucet Brushed Nickel', quantity: 3, unitPrice: 129.99, total: 389.97, category: 'DECOR' },
      { name: 'Porcelain Tile 12x24 (45 sqft/box)', quantity: 8, unitPrice: 89.99, total: 719.92, category: 'FINISHES' },
      { name: 'Mosaic Backsplash Sheet 12x12"', quantity: 15, unitPrice: 12.99, total: 194.85, category: 'DECOR' },
      { name: 'Toilet Elongated White', quantity: 3, unitPrice: 199.99, total: 599.97, category: 'DECOR' },
    ],
    createdAt: '2026-03-20T13:00:00Z',
  },
  {
    id: 'r7', store: 'Home Depot — Frisco', date: '2026-03-22', subtotal: 876.50, tax: 72.31, total: 948.81, paymentMethod: 'Account #HD-88912',
    items: [
      { name: 'LED Recessed Light 6" 6-pack', quantity: 4, unitPrice: 54.99, total: 219.96, category: 'ELECTRICAL' },
      { name: 'Dimmer Switch 3-way', quantity: 6, unitPrice: 24.99, total: 149.94, category: 'ELECTRICAL' },
      { name: 'Door Lever Handle Satin Nickel', quantity: 8, unitPrice: 29.99, total: 239.92, category: 'MISC' },
      { name: 'House Numbers Modern 5"', quantity: 4, unitPrice: 9.99, total: 39.96, category: 'MISC' },
      { name: 'Mailbox Wall Mount Black', quantity: 1, unitPrice: 64.99, total: 64.99, category: 'MISC' },
      { name: 'Door Stopper Magnetic (5-pack)', quantity: 2, unitPrice: 18.99, total: 37.98, category: 'MISC' },
      { name: 'Smoke Detector Combo 3-pack', quantity: 1, unitPrice: 89.99, total: 89.99, category: 'ELECTRICAL' },
      { name: 'Weatherstrip Door Kit', quantity: 2, unitPrice: 16.88, total: 33.76, category: 'MISC' },
    ],
    createdAt: '2026-03-22T10:30:00Z',
  },
]

// ── EXPENSE CATEGORY CONFIG ──────────────────────

const DEFAULT_EXPENSE_CATEGORIES: Record<string, { en: string; es: string; color: string }> = {
  FRAMING:     { en: 'Framing',     es: 'Framing',      color: '#3B82F6' },
  ROOFING:     { en: 'Roofing',     es: 'Techo',        color: '#EF4444' },
  INSULATION:  { en: 'Insulation',  es: 'Aislamiento',  color: '#F59E0B' },
  PLUMBING:    { en: 'Plumbing',    es: 'Plomería',     color: '#06B6D4' },
  FOUNDATION:  { en: 'Foundation',  es: 'Cimentación',  color: '#00BCD4' },
  HARDWARE:    { en: 'Hardware',    es: 'Ferretería',   color: '#6B7280' },
  ELECTRICAL:  { en: 'Electrical',  es: 'Eléctrico',    color: '#EC4899' },
  DRYWALL:     { en: 'Drywall',     es: 'Drywall',      color: '#14B8A6' },
  FINISHES:    { en: 'Finishes',    es: 'Acabados',     color: '#F97316' },
  DECOR:       { en: 'Decor & Fixtures', es: 'Decoración y Accesorios', color: '#A855F7' },
  MISC:        { en: 'Miscellaneous', es: 'Misceláneos', color: '#64748B' },
  OTHER:       { en: 'Other',       es: 'Otro',         color: '#9CA3AF' },
}

// ── COMPONENT ─────────────────────────────────────

export default function ReceiptsModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const isEs = i18n.language === 'es'
  const [uploading, setUploading] = useState(false)
  const [latestScan, setLatestScan] = useState<ReceiptData | null>(null)
  // Data-quality warnings from the last scan (merchant needs review, totals
  // mismatch, quantity defaulted, total recomputed). Shown inline, never alert.
  const [scanValidation, setScanValidation] = useState<
    { storeNeedsReview?: boolean; totalsMismatch?: boolean; warnings?: string[] } | null
  >(null)
  // View-original-file (Phase 2a). Which receipt is currently opening, plus a
  // bilingual inline error when the authed open fails (no alert()).
  const [openingFileId, setOpeningFileId] = useState<string | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [view, setView] = useState<'receipts' | 'summary' | 'payment'>('summary')
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'category'>('date')
  // PR-NEW: optional payment-method filter. When set, the
  // receipts list + summary sections only show receipts whose
  // derived payment key matches. Click a row in the payment
  // summary or a chip to toggle it.
  const [paymentFilter, setPaymentFilter] = useState<string | null>(null)
  // Plan gating is server-authoritative via /api/subscription. Falls
  // back to STARTER while loading or on error — never to PRO. The
  // legacy `const demoPlan: string = 'PRO'` constant was removed; demo
  // toggling still works inside `useSubscriptionPlan` when isDemo.
  const { effectivePlan } = useSubscriptionPlan()
  const [showAddCategory, setShowAddCategory] = useState(false)
  const [newCatName, setNewCatName] = useState('')
  const [newCatNameEs, setNewCatNameEs] = useState('')

  // Custom categories persist in localStorage
  const [customCategories, setCustomCategories] = useState<Record<string, { en: string; es: string; color: string }>>(() => {
    try {
      const saved = localStorage.getItem(`constructai-custom-categories-${projectId}`)
      return saved ? JSON.parse(saved) : {}
    } catch { return {} }
  })

  const CUSTOM_COLORS = ['#0891B2', '#00BCD4', '#DB2777', '#059669', '#D97706', '#DC2626', '#4338CA', '#0D9488', '#BE185D', '#B45309']

  const addCustomCategory = () => {
    if (!newCatName.trim()) return
    const key = newCatName.trim().toUpperCase().replace(/\s+/g, '_')
    const colorIndex = Object.keys(customCategories).length % CUSTOM_COLORS.length
    const updated = {
      ...customCategories,
      [key]: { en: newCatName.trim(), es: newCatNameEs.trim() || newCatName.trim(), color: CUSTOM_COLORS[colorIndex] }
    }
    setCustomCategories(updated)
    localStorage.setItem(`constructai-custom-categories-${projectId}`, JSON.stringify(updated))
    setNewCatName('')
    setNewCatNameEs('')
    setShowAddCategory(false)
  }

  // Merge default + custom categories into a single lookup
  const expenseCategories: Record<string, { en: string; es: string; color: string }> = { ...DEFAULT_EXPENSE_CATEGORIES, ...customCategories }

  // PR-NEW: error state surfaced as a local banner (not the
  // global ErrorBoundary). The previous `r => r.json()` had no
  // `res.ok` check; an API failure cascaded into the rest of the
  // module, where `.toFixed` on undefined item fields crashed.
  const {
    data: apiReceipts,
    isLoading,
    isError: isReceiptsError,
  } = useQuery<ReceiptData[]>({
    queryKey: ['receipts', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/receipts`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      // PR3 (C7): 402 = plan gate. The PRO card explains it; do not also
      // show "Could not load receipts" next to it.
      if (isPlanGateStatus(res.status)) return []
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.error || `receipts_fetch_failed_${res.status}`)
      }
      return res.json()
    },
    retry: 1,
  })

  // PR-NEW: every receipt + its items run through the
  // normalizer so legacy AI-shaped rows (`description` /
  // `totalPrice`) and current FE-shaped rows (`name` / `total`)
  // both render without crashing `.toFixed`.
  const allReceipts: ReceiptData[] = Array.isArray(apiReceipts)
    ? apiReceipts.map(normalizeReceipt)
    : (isDemoProject(projectId) ? DEMO_RECEIPTS : [])
  const isDemo = isDemoProject(projectId)

  // PR-NEW: payment-method-aware filtered view. Each receipt
  // carries a parsed `{ key, label, ... }` derived from its
  // existing `paymentMethod` string — no schema change, no
  // raw card numbers stored or rendered.
  const receiptsWithPayment = allReceipts.map((r) => ({
    receipt: r,
    payment: parsePaymentMethod(r.paymentMethod, isEs),
  }))
  const receipts: ReceiptData[] = paymentFilter
    ? receiptsWithPayment
        .filter(({ payment }) => payment.key === paymentFilter)
        .map(({ receipt }) => receipt)
    : allReceipts

  // PR-NEW: upload error displayed as a local banner instead of
  // a browser `alert()` (which is jarring + non-bilingual).
  const [uploadError, setUploadError] = useState<string | null>(null)

  const scanMutation = useMutation({
    mutationFn: async (file: File) => {
      const token = await getToken()
      setUploading(true)
      setUploadError(null)
      const formData = new FormData()
      formData.append('file', file)
      const res = await apiFetch(`/api/projects/${projectId}/receipts/scan`, { method: 'POST', headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }, body: formData })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || `Upload failed (${res.status})`)
      }
      const data = await res.json()
      if (data?.receipt) data.receipt = normalizeReceipt(data.receipt)
      return data
    },
    onSuccess: (data) => {
      setLatestScan(data?.receipt ?? data)
      setScanValidation(data?.validation ?? null)
      queryClient.invalidateQueries({ queryKey: ['receipts', projectId] })
      setUploading(false)
    },
    onError: (err: Error) => {
      setUploading(false)
      // PR3 (C7): never show a raw server code ("plan_required").
      setUploadError(
        friendlyApiError(err.message, isEs, isEs ? 'No se pudo subir el recibo' : 'Could not upload receipt'),
      )
    }
  })

  // Open the ORIGINAL uploaded receipt file (PDF/photo) so the user can compare
  // the AI-extracted merchant/items/totals against the source. A plain
  // <a href> would skip the Clerk Authorization header → 401, so we fetch with
  // the bearer token, turn the response into a Blob URL, and open it in a new
  // tab — exactly the BidsModule.openOriginalFile pattern. Read-only: no save,
  // no write to any other module. Failures surface inline (never alert()).
  const openReceiptFile = async (receipt: ReceiptData) => {
    if (!projectId || !receipt.storagePath) return
    setOpeningFileId(receipt.id)
    setFileError(null)
    let blobUrl: string | null = null
    try {
      const token = await getToken()
      const res = await apiFetch(
        `/api/projects/${projectId}/receipts/${receipt.id}/file`,
        { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
      )
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || `HTTP ${res.status}`)
      }
      const blob = await res.blob()
      blobUrl = URL.createObjectURL(blob)
      window.open(blobUrl, '_blank', 'noopener,noreferrer')
      // Revoke after a minute so the new tab has time to load the object URL.
      const toRevoke = blobUrl
      setTimeout(() => URL.revokeObjectURL(toRevoke), 60_000)
    } catch {
      if (blobUrl) URL.revokeObjectURL(blobUrl)
      setFileError(
        isEs
          ? 'No se pudo abrir el recibo original. Inténtalo de nuevo.'
          : 'Could not open the original receipt. Please try again.',
      )
    } finally {
      setOpeningFileId(null)
    }
  }

  // ── Expense calculations ──────────────────────
  const totalExpenses = receipts.reduce((sum, r) => sum + (r.total || 0), 0)
  const totalTax = receipts.reduce((sum, r) => sum + (r.tax || 0), 0)

  // Flatten all items from all receipts with receipt metadata
  const allItems = receipts.flatMap(r =>
    (r.items || []).map(item => ({
      ...item,
      store: r.store || '',
      date: r.date || r.createdAt,
      paymentMethod: r.paymentMethod || '',
      receiptId: r.id,
    }))
  )

  // Group by category
  const categoryTotals = allItems.reduce((acc, item) => {
    const cat = item.category || 'OTHER'
    acc[cat] = (acc[cat] || 0) + item.total
    return acc
  }, {} as Record<string, number>)

  const sortedCategories = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1])
  const maxCategoryTotal = Math.max(...Object.values(categoryTotals), 1)

  // PR-NEW: aggregate by parsed payment method across ALL
  // receipts (not the filtered view — the summary is the
  // global picture). Each entry pairs the canonical key with
  // a label + total. Sorted descending by total. Keys stay
  // stable so click-to-filter is deterministic.
  const paymentTotalsByKey = receiptsWithPayment.reduce((acc, { receipt, payment }) => {
    const existing = acc.get(payment.key)
    const total = safeNumber(receipt.total)
    const count = (existing?.count ?? 0) + 1
    acc.set(payment.key, {
      key: payment.key,
      label: payment.label,
      brand: payment.brand,
      last4: payment.last4,
      total: (existing?.total ?? 0) + total,
      count,
    })
    return acc
  }, new Map<string, { key: string; label: string; brand: string; last4: string | null; total: number; count: number }>())
  const sortedPaymentGroups = Array.from(paymentTotalsByKey.values()).sort((a, b) => b.total - a.total)
  const maxPaymentGroupTotal = Math.max(...sortedPaymentGroups.map((g) => g.total), 1)
  // PR-NEW: percentage denominator for the By-card view must
  // use the UNFILTERED grand total. The KPI `totalExpenses`
  // (filtered) is correct for the top-card stat but produced
  // a 392% bug for the payment-group percentages: when the
  // user selected "Desconocido", every group still rendered
  // (sortedPaymentGroups comes from `receiptsWithPayment` →
  // `allReceipts`, so the rows themselves are unfiltered),
  // but the divisor shrank to just the selected group's
  // total. EFT/Debit's $541.20 ÷ Desconocido's $138.22 ×
  // 100 ≈ 392%. Using paymentSummaryTotal (always the full
  // unfiltered total) fixes the math without changing the
  // visible row set.
  const paymentSummaryTotal = sortedPaymentGroups.reduce((sum, g) => sum + g.total, 0)

  // Sort all items for the line-item table
  const sortedItems = [...allItems].sort((a, b) => {
    if (sortBy === 'date') return new Date(b.date).getTime() - new Date(a.date).getTime()
    if (sortBy === 'amount') return b.total - a.total
    return (a.category || '').localeCompare(b.category || '')
  })

  // PR-NEW: safe date format — handles undefined/null/invalid
  // input by returning an em-dash placeholder instead of "Invalid Date".
  const formatDate = (d: string | null | undefined) => {
    if (!d) return '—'
    const parsed = new Date(d)
    if (Number.isNaN(parsed.getTime())) return '—'
    // PR3 (C5): a receipt date is a calendar day — read it by its UTC day so
    // Dallas no longer shows it one day early.
    return formatDateOnly(d, isEs ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric' })
  }

  return (
    <div className="p-5 max-w-5xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">
            {isEs ? 'Recibos y Gastos' : 'Receipts & Expenses'}
          </h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {isDemo
              ? (isEs ? 'Vista de solo lectura' : 'Read-only preview')
              : `${receipts.length} ${isEs ? 'recibos' : 'receipts'} · ${allItems.length} ${isEs ? 'artículos' : 'items'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* PR #138C: CSV download hidden in demo so no demo totals
              get exported as if they were real spend. Stays available
              on real projects whenever there are items to export. */}
          {!isDemo && allItems.length > 0 && (
            <button
              onClick={() => downloadCsv({
                filename: `gastos-proyecto-${projectId}`,
                headers: [isEs ? 'Fecha' : 'Date', isEs ? 'Artículo' : 'Item', isEs ? 'Categoría' : 'Category', isEs ? 'Tienda' : 'Store', isEs ? 'Cant.' : 'Qty', isEs ? 'Precio' : 'Price', 'Total'],
                rows: allItems.map(i => [i.date ? formatDateOnly(i.date, isEs ? 'es-MX' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }, '') : '', i.name, i.category || '', i.store, i.quantity, i.unitPrice, i.total])
              })}
              className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#F2F0EB] transition-colors"
            >
              <Download size={14} />
              {isEs ? 'Descargar CSV' : 'Download CSV'}
            </button>
          )}
          {/* PR #138A: receipt scan is an AI mutation against the
              real backend; gate the trigger button and file input
              for demo so no scan call ever fires. */}
          {!isDemo && (
            <>
              <button
                onClick={() => fileRef.current?.click()}
                className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#14457E] transition-colors"
              >
                <Upload size={14} />
                {isEs ? 'Subir recibo' : 'Upload receipt'}
              </button>
            </>
          )}
        </div>
        {!isDemo && (
          <input ref={fileRef} type="file" accept="image/*,.pdf" capture="environment"
            onChange={e => { const f = e.target.files?.[0]; if (f) scanMutation.mutate(f) }} className="hidden" />
        )}
      </div>

      {/* PR #138C: demo Receipts renders only the saved-project notice
          below the header. The operational surface (KPI row, real-error
          banner, view toggle, category breakdown with "+ Agregar
          categoría", items table, receipts list) is intentionally
          omitted because demo has no real spend data and the GET
          `/api/projects/demo-frisco-01/receipts` 404s. The same
          testId from PR #138A is preserved so the existing regression
          tests in demoCriticalGuards.smoke.ts and
          demoActionGuards138B.smoke.ts continue to pass.
          Real-project branch is the original PR #138A real branch. */}
      {isDemo ? (
        <SavedProjectOnlyNotice
          testId="receipts-upload-demo-notice"
          bodyEn="Receipt scanning is available in saved projects. Create or open a real project to upload receipts."
          bodyEs="El escaneo de recibos está disponible en proyectos guardados. Crea o abre un proyecto real para subir recibos."
        />
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-[#ECEAE4] rounded-xl p-3 border-l-[3px] border-l-[#06539B]">
              <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">
                {isEs ? 'Total Gastos' : 'Total Expenses'}
              </div>
              <div className="text-[22px] font-[900] tracking-[-1px] text-[#1C1A17]">
                ${totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
              <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">
                {isEs ? 'Impuestos' : 'Tax Paid'}
              </div>
              <div className="text-[18px] font-[900] tracking-[-0.5px] text-[#9E9890]">
                ${totalTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
              <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">
                {isEs ? 'Recibos' : 'Receipts'}
              </div>
              <div className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{receipts.length}</div>
            </div>
            <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
              <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">
                {isEs ? 'Artículos' : 'Line Items'}
              </div>
              <div className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{allItems.length}</div>
            </div>
          </div>

          {/* Upload zone — only in real projects (PR #138A real branch). */}
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-[#ECEAE4] rounded-xl p-5 text-center cursor-pointer hover:border-[#06539B] transition-all"
          >
            {uploading ? (
              <div className="flex items-center justify-center gap-3">
                <Loader2 size={20} className="text-[#06539B] animate-spin" />
                <span className="text-[12px] font-[700]">{isEs ? 'Subiendo recibo…' : 'Uploading receipt…'}</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-3">
                <Receipt size={20} className="text-[#9E9890]" />
                <span className="text-[12px] font-[700] text-[#9E9890]">
                  {isEs ? 'Foto o PDF — la IA extrae tienda, artículos y total automáticamente' : 'Photo or PDF — AI extracts store, items & total automatically'}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {/* PR #138C: everything below this point is real-project-only.
          The fragment wrapper is closed at end-of-render. */}
      {!isDemo && (
      <>{/* === REAL-PROJECT-ONLY SUBTREE BEGIN === */}

      {/* PR-NEW: local error banners. Replaces the previous
          browser alert() so the message stays bilingual + in-app
          and doesn't trip the global ErrorBoundary. */}
      {uploadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-[700] text-red-700">
          {uploadError}
        </div>
      )}
      {scanValidation && (scanValidation.storeNeedsReview || scanValidation.totalsMismatch || (scanValidation.warnings?.length ?? 0) > 0) && (
        <div data-testid="receipt-scan-warnings" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-[600] text-amber-800 space-y-1">
          {scanValidation.storeNeedsReview && (
            <div className="font-[800]">{isEs ? 'Revisar tienda detectada' : 'Review detected store'}</div>
          )}
          {scanValidation.totalsMismatch && (
            <div>{isEs ? 'El total no coincide con la suma de partidas + impuesto — revísalo.' : "The total doesn't match the sum of line items + tax — please review."}</div>
          )}
          {scanValidation.warnings?.includes('QUANTITY_DEFAULTED') && (
            <div>{isEs ? 'Una cantidad faltante se ajustó a 1. Verifica las cantidades.' : 'A missing quantity was set to 1. Please verify quantities.'}</div>
          )}
          {scanValidation.warnings?.includes('TOTAL_RECOMPUTED') && (
            <div>{isEs ? 'El total se recalculó a partir de las partidas.' : 'The total was recalculated from the line items.'}</div>
          )}
          <button onClick={() => setScanValidation(null)} className="text-[10px] font-[800] text-amber-700 underline">
            {isEs ? 'Entendido' : 'Got it'}
          </button>
        </div>
      )}
      {isReceiptsError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-[700] text-amber-800">
          {isEs ? 'No se pudieron cargar los recibos.' : 'Could not load receipts.'}
        </div>
      )}

      {/* View toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setView('summary')}
          className={`flex items-center gap-1.5 text-[13px] font-[800] px-4 py-2 rounded-full transition-colors ${
            view === 'summary' ? 'bg-[#06539B] text-white' : 'bg-white border border-[#ECEAE4] text-[#9E9890] hover:text-[#1C1A17]'
          }`}
        >
          <BarChart3 size={13} />
          {isEs ? 'Hoja de gastos' : 'Expense sheet'}
          {effectivePlan === 'STARTER' && <ProBadge />}
        </button>
        <button
          onClick={() => setView('payment')}
          className={`flex items-center gap-1.5 text-[13px] font-[800] px-4 py-2 rounded-full transition-colors ${
            view === 'payment' ? 'bg-[#06539B] text-white' : 'bg-white border border-[#ECEAE4] text-[#9E9890] hover:text-[#1C1A17]'
          }`}
        >
          <CreditCard size={13} />
          {isEs ? 'Por tarjeta' : 'By card'}
        </button>
        <button
          onClick={() => setView('receipts')}
          className={`flex items-center gap-1.5 text-[13px] font-[800] px-4 py-2 rounded-full transition-colors ${
            view === 'receipts' ? 'bg-[#06539B] text-white' : 'bg-white border border-[#ECEAE4] text-[#9E9890] hover:text-[#1C1A17]'
          }`}
        >
          <LayoutList size={13} />
          {isEs ? 'Recibos' : 'Receipts'}
        </button>

        {/* PR-NEW: active payment-method filter chip. When a
            row in the payment summary is clicked, this chip
            shows + the underlying receipts/items lists narrow
            to that group. Clicking the X clears the filter. */}
        {paymentFilter && (() => {
          const active = sortedPaymentGroups.find((g) => g.key === paymentFilter)
          return (
            <button
              onClick={() => setPaymentFilter(null)}
              className="flex items-center gap-1.5 text-[11px] font-[700] px-3 py-1 rounded-full bg-[#FFFBEB] border border-[#FDE68A] text-[#B45309] hover:bg-[#FEF3C7]"
              aria-label={isEs ? 'Quitar filtro de pago' : 'Clear payment filter'}
            >
              <span>{isEs ? 'Filtro:' : 'Filter:'}</span>
              <span className="font-[800]">{active?.label || paymentFilter}</span>
              <span aria-hidden="true">×</span>
            </button>
          )
        })()}
      </div>

      {/* ━━━ EXPENSE SUMMARY SHEET ━━━━━━━━━━━━━━━━━━ */}
      {view === 'summary' && (
        <ProFeatureGate requiredPlan="PRO" currentPlan={effectivePlan} featureName="Expense Sheet" featureNameEs="Hoja de Gastos" isEs={isEs}>
        <div>
        <div className="space-y-4">
          {/* Category breakdown with bars */}
          <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-[#ECEAE4] flex items-center justify-between">
              <h3 className="text-[13px] font-[800]">{isEs ? 'Gastos por Categoría' : 'Expenses by Category'}</h3>
              <button
                onClick={() => setShowAddCategory(!showAddCategory)}
                className="text-[10px] font-[800] text-[#06539B] hover:text-[#14457E] transition-colors"
              >
                + {isEs ? 'Agregar categoría' : 'Add category'}
              </button>
            </div>

            {/* Add custom category form */}
            {showAddCategory && (
              <div className="px-4 py-3 border-b border-[#ECEAE4] bg-[#FAFAF8]">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="text-[9px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
                      {isEs ? 'Nombre (EN)' : 'Name (EN)'}
                    </label>
                    <input
                      type="text"
                      value={newCatName}
                      onChange={e => setNewCatName(e.target.value)}
                      placeholder={isEs ? 'Ej: Landscaping' : 'e.g. Landscaping'}
                      className="mt-0.5 w-full px-2.5 py-2 text-[11px] font-[600] border border-[#ECEAE4] rounded-lg focus:outline-none focus:border-[#06539B]"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-[9px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
                      {isEs ? 'Nombre (ES)' : 'Name (ES)'}
                    </label>
                    <input
                      type="text"
                      value={newCatNameEs}
                      onChange={e => setNewCatNameEs(e.target.value)}
                      placeholder={isEs ? 'Ej: Jardinería' : 'e.g. Landscaping'}
                      className="mt-0.5 w-full px-2.5 py-2 text-[11px] font-[600] border border-[#ECEAE4] rounded-lg focus:outline-none focus:border-[#06539B]"
                    />
                  </div>
                  <button
                    onClick={addCustomCategory}
                    disabled={!newCatName.trim()}
                    className="text-[10px] font-[800] text-white bg-[#06539B] px-4 py-2 rounded-lg hover:bg-[#14457E] transition-colors disabled:opacity-40 whitespace-nowrap"
                  >
                    {isEs ? 'Agregar' : 'Add'}
                  </button>
                  <button
                    onClick={() => setShowAddCategory(false)}
                    className="text-[12px] font-[700] text-[#9E9890] px-2 py-2 hover:text-[#1C1A17]"
                  >
                    ✕
                  </button>
                </div>
                {Object.keys(customCategories).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {Object.entries(customCategories).map(([key, cat]) => (
                      <span key={key} className="inline-flex items-center gap-1 text-[9px] font-[700] px-2 py-0.5 rounded-full bg-white border border-[#ECEAE4]">
                        <span className="w-[6px] h-[6px] rounded-sm" style={{ background: cat.color }} />
                        {isEs ? cat.es : cat.en}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="p-4 space-y-3">
              {sortedCategories.map(([cat, total]) => {
                const config = expenseCategories[cat] || DEFAULT_EXPENSE_CATEGORIES.OTHER
                const pct = Math.round((total / totalExpenses) * 100)
                return (
                  <div key={cat}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className="w-[10px] h-[10px] rounded-sm" style={{ background: config.color }} />
                        <span className="text-[12px] font-[700]">{isEs ? config.es : config.en}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[12px] font-[700] text-[#9E9890]">{pct}%</span>
                        <span className="text-[12px] font-[800] text-[#1C1A17]">${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                    <div className="h-[6px] bg-[#F2F0EB] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${(total / maxCategoryTotal) * 100}%`, background: config.color }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Full line-item table */}
          <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-[#ECEAE4] flex items-center justify-between">
              <h3 className="text-[13px] font-[800]">{isEs ? 'Detalle de Todos los Gastos' : 'All Expenses Detail'}</h3>
              <div className="flex items-center gap-2">
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as any)}
                  className="text-[10px] font-[700] border border-[#ECEAE4] rounded-lg px-2 py-1 bg-white text-[#9E9890]"
                >
                  <option value="date">{isEs ? 'Fecha' : 'Date'}</option>
                  <option value="amount">{isEs ? 'Monto' : 'Amount'}</option>
                  <option value="category">{isEs ? 'Categoría' : 'Category'}</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-[9px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] bg-[#FAFAF8]">
                    <th className="text-left px-4 py-2.5">{isEs ? 'Fecha' : 'Date'}</th>
                    <th className="text-left px-4 py-2.5">{isEs ? 'Artículo' : 'Item'}</th>
                    <th className="text-left px-4 py-2.5">{isEs ? 'Categoría' : 'Category'}</th>
                    <th className="text-left px-4 py-2.5">{isEs ? 'Tienda' : 'Store'}</th>
                    <th className="text-right px-4 py-2.5">{isEs ? 'Cant.' : 'Qty'}</th>
                    <th className="text-right px-4 py-2.5">{isEs ? 'Precio' : 'Price'}</th>
                    <th className="text-right px-4 py-2.5">{isEs ? 'Total' : 'Total'}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item, i) => {
                    const catConfig = expenseCategories[item.category || 'OTHER'] || DEFAULT_EXPENSE_CATEGORIES.OTHER
                    return (
                      <tr key={`${item.receiptId}-${i}`} className="border-t border-[#F2F0EB] hover:bg-[#FAFAF8] transition-colors">
                        <td className="px-4 py-2 text-[#9E9890] font-[600] whitespace-nowrap">{formatDate(item.date)}</td>
                        <td className="px-4 py-2 font-[700] text-[#1C1A17]">{item.name}</td>
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center gap-1">
                            <span className="w-[7px] h-[7px] rounded-sm" style={{ background: catConfig.color }} />
                            <span className="text-[12px] font-[700] text-[#9E9890]">{isEs ? catConfig.es : catConfig.en}</span>
                          </span>
                        </td>
                        <td className="px-4 py-2 text-[10px] font-[600] text-[#9E9890] whitespace-nowrap">{item.store}</td>
                        <td className="px-4 py-2 text-right text-[#9E9890] font-[600]">{safeNumber(item.quantity)}</td>
                        <td className="px-4 py-2 text-right text-[#9E9890] font-[600]">${safeMoney(item.unitPrice)}</td>
                        <td className="px-4 py-2 text-right font-[800] text-[#1C1A17]">${safeMoney(item.total)}</td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[#ECEAE4] bg-[#FAFAF8]">
                    <td colSpan={6} className="px-4 py-2.5 text-right text-[10px] font-[800] text-[#9E9890] uppercase">
                      {isEs ? 'Total materiales' : 'Total materials'}
                    </td>
                    <td className="px-4 py-2.5 text-right text-[14px] font-[900] text-[#1C1A17]">
                      ${allItems.reduce((s, i) => s + safeNumber(i.total), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
        </div>
        </ProFeatureGate>
      )}

      {/* ━━━ EXPENSES BY PAYMENT METHOD ━━━━━━━━━━━━━━
          Groups every receipt by its parsed payment method
          (Visa •••• 1234, Cash, Check, Account, …). Clicking
          a row toggles a global payment filter that narrows
          the receipts list + the other summaries. The parser
          reads the existing free-form `paymentMethod` string
          — no schema change, no full card numbers ever
          rendered. */}
      {view === 'payment' && (
        <ProFeatureGate
          requiredPlan="PRO"
          currentPlan={effectivePlan}
          featureName="Payment summary"
          featureNameEs="Resumen por pago"
          isEs={isEs}
        >
          <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-[#ECEAE4] flex items-center justify-between">
              <h3 className="text-[13px] font-[800]">
                {isEs ? 'Gastos por forma de pago' : 'Expenses by payment method'}
              </h3>
              <span className="text-[10px] font-[700] text-[#9E9890]">
                {sortedPaymentGroups.length} {isEs ? 'métodos' : 'methods'}
              </span>
            </div>
            {sortedPaymentGroups.length === 0 ? (
              <div className="px-4 py-6 text-[11px] font-[600] text-[#9E9890] text-center">
                {isEs ? 'Aún no hay datos de pago.' : 'No payment data yet.'}
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {sortedPaymentGroups.map((g) => {
                  const isActive = paymentFilter === g.key
                  // PR-NEW: divide against the unfiltered total
                  // so an active paymentFilter doesn't make
                  // other rows exceed 100%.
                  const pct = paymentSummaryTotal > 0 ? Math.round((g.total / paymentSummaryTotal) * 100) : 0
                  return (
                    <button
                      key={g.key}
                      type="button"
                      onClick={() => setPaymentFilter(isActive ? null : g.key)}
                      aria-pressed={isActive}
                      aria-label={isEs ? `Filtrar por ${g.label}` : `Filter by ${g.label}`}
                      className={`w-full text-left rounded-lg px-3 py-2 transition-colors ${
                        isActive ? 'bg-[#06539B]/5 ring-1 ring-[#06539B]/30' : 'hover:bg-[#FAFAF8]'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <CreditCard size={12} className="text-[#06539B]" />
                          <span className="text-[12px] font-[800] text-[#1C1A17]">{g.label}</span>
                          <span className="text-[10px] font-[600] text-[#9E9890]">
                            · {g.count} {isEs ? (g.count === 1 ? 'recibo' : 'recibos') : (g.count === 1 ? 'receipt' : 'receipts')}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-[11px] font-[700] text-[#9E9890]">{pct}%</span>
                          <span className="text-[13px] font-[900] text-[#1C1A17]">
                            ${g.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                      <div className="h-[6px] bg-[#F2F0EB] rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[#06539B] transition-all duration-500"
                          style={{ width: `${(g.total / maxPaymentGroupTotal) * 100}%` }}
                        />
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
            <div className="px-4 py-2 border-t border-[#ECEAE4] text-[10px] font-[600] text-[#9E9890]">
              {isEs
                ? 'Solo se muestran los últimos 4 dígitos extraídos del recibo. Nunca el número completo.'
                : 'Only the last 4 digits extracted from the receipt are shown. Full card numbers are never displayed.'}
            </div>
          </div>

          {/* PR-NEW: payment-method detail breakdown.
              When a payment row is selected, list the receipts
              that compose that total — store / date / total /
              tax / item count + a compact item summary. Pure
              FE derivation from already-loaded receipts; no AI
              calls, no rescans, no backend hits. */}
          {paymentFilter && (() => {
            const activeGroup = sortedPaymentGroups.find((g) => g.key === paymentFilter)
            const groupReceipts = receiptsWithPayment
              .filter(({ payment }) => payment.key === paymentFilter)
              .map(({ receipt }) => receipt)
              .sort((a, b) => {
                const ta = a.date ? new Date(a.date).getTime() : new Date(a.createdAt).getTime()
                const tb = b.date ? new Date(b.date).getTime() : new Date(b.createdAt).getTime()
                return tb - ta
              })
            return (
              <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden mt-4">
                <div className="px-4 py-3 border-b border-[#ECEAE4] flex items-center justify-between">
                  <h3 className="text-[13px] font-[800]">
                    {isEs ? 'Recibos pagados con' : 'Receipts paid with'}{' '}
                    <span className="text-[#06539B]">{activeGroup?.label || paymentFilter}</span>
                  </h3>
                  <button
                    onClick={() => setPaymentFilter(null)}
                    className="text-[11px] font-[700] text-[#9E9890] hover:text-[#1C1A17]"
                    aria-label={isEs ? 'Quitar filtro de pago' : 'Clear payment filter'}
                  >
                    {isEs ? 'Quitar filtro' : 'Clear filter'}
                  </button>
                </div>
                {groupReceipts.length === 0 ? (
                  <div className="px-4 py-6 text-[11px] font-[600] text-[#9E9890] text-center">
                    {isEs
                      ? 'No hay recibos para esta forma de pago.'
                      : 'No receipts for this payment method.'}
                  </div>
                ) : (
                  <div className="divide-y divide-[#F2F0EB]">
                    {groupReceipts.map((r) => {
                      const itemCount = Array.isArray(r.items) ? r.items.length : 0
                      const preview = (r.items || []).slice(0, 3)
                      return (
                        <div key={r.id} className="px-4 py-3 hover:bg-[#FAFAF8]">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-[13px] font-[800] text-[#1C1A17] truncate">
                                {r.store || (isEs ? 'Recibo' : 'Receipt')}
                              </div>
                              <div className="flex flex-wrap items-center gap-2 mt-0.5 text-[10px] font-[600] text-[#9E9890]">
                                <span>{formatDate(r.date || r.createdAt)}</span>
                                <span>·</span>
                                <span>
                                  {itemCount} {isEs
                                    ? (itemCount === 1 ? 'artículo' : 'artículos')
                                    : (itemCount === 1 ? 'item' : 'items')}
                                </span>
                                {r.tax != null && (
                                  <>
                                    <span>·</span>
                                    <span>
                                      {isEs ? 'Imp.' : 'Tax'}: ${safeMoney(r.tax)}
                                    </span>
                                  </>
                                )}
                              </div>
                              {preview.length > 0 && (
                                <div className="mt-1 text-[10px] font-[600] text-[#9E9890] truncate">
                                  {preview.map((it) => it.name).filter(Boolean).join(' · ')}
                                  {itemCount > preview.length && (
                                    <> · +{itemCount - preview.length} {isEs ? 'más' : 'more'}</>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-[14px] font-[900] text-[#1C1A17]">
                                ${safeMoney(r.total)}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })()}
        </ProFeatureGate>
      )}

      {/* ━━━ RECEIPTS LIST VIEW ━━━━━━━━━━━━━━━━━━━━ */}
      {view === 'receipts' && (
        <div className="space-y-3">
          {/* Inline error when opening the original file failed (no alert()). */}
          {fileError && (
            <div
              data-testid="receipt-file-error"
              className="flex items-center justify-between gap-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              <span className="text-[11px] font-[700] text-red-600">{fileError}</span>
              <button
                onClick={() => setFileError(null)}
                className="text-[10px] font-[800] text-red-500 underline whitespace-nowrap"
              >
                {isEs ? 'Cerrar' : 'Dismiss'}
              </button>
            </div>
          )}
          {receipts.map(receipt => (
            <div key={receipt.id} className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
              {/* Receipt header */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-[#F2F0EB]">
                <div className="w-[36px] h-[36px] rounded-lg bg-[#F2F0EB] flex items-center justify-center flex-shrink-0">
                  <Receipt size={16} className="text-[#9E9890]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-[800] text-[#1C1A17] truncate">{receipt.store || t('Receipt')}</div>
                  <div className="flex items-center gap-3 text-[10px] font-[600] text-[#9E9890]">
                    {receipt.date && <span className="flex items-center gap-1"><Calendar size={10} /> {formatDate(receipt.date)}</span>}
                    {receipt.paymentMethod && <span className="flex items-center gap-1"><CreditCard size={10} /> {receipt.paymentMethod}</span>}
                    <span>{receipt.items?.length || 0} {isEs ? 'artículos' : 'items'}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[16px] font-[900] text-[#1C1A17]">${(receipt.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                  {receipt.tax != null && (
                    <div className="text-[9px] font-[600] text-[#9E9890]">{isEs ? 'imp.' : 'tax'} ${safeMoney(receipt.tax)}</div>
                  )}
                </div>
                {/* View the ORIGINAL uploaded file (PDF/photo) when one was
                    stored. Gated on storagePath — otherwise show an inline
                    "no file" note so the user knows nothing is missing. */}
                {receipt.storagePath ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); openReceiptFile(receipt) }}
                    disabled={openingFileId === receipt.id}
                    className="p-1.5 rounded-full hover:bg-[#06539B]/10 transition-colors ml-1 disabled:opacity-50"
                    title={isEs ? 'Ver recibo subido' : 'View uploaded receipt'}
                    aria-label={isEs ? 'Ver recibo subido' : 'View uploaded receipt'}
                  >
                    {openingFileId === receipt.id
                      ? <Loader2 size={14} className="text-[#06539B] animate-spin" />
                      : <Eye size={14} className="text-[#9E9890] hover:text-[#06539B] transition-colors" />}
                  </button>
                ) : (
                  <span
                    className="text-[9px] font-[700] text-[#C4BFB7] ml-1 whitespace-nowrap"
                    title={isEs ? 'Archivo original no disponible' : 'Original file unavailable'}
                  >
                    {isEs ? 'Sin archivo' : 'No file'}
                  </span>
                )}
                <button
                  onClick={async (e) => {
                    e.stopPropagation()
                    try {
                      const token = await getToken()
                      await apiFetch(`/api/projects/${projectId}/receipts/${receipt.id}`, {
                        method: 'DELETE',
                        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
                      })
                      queryClient.invalidateQueries({ queryKey: ['receipts', projectId] })
                    } catch { /* silent */ }
                  }}
                  className="p-1.5 rounded-full hover:bg-red-50 transition-colors ml-1"
                  title={isEs ? 'Eliminar recibo' : 'Delete receipt'}
                >
                  <Trash2 size={14} className="text-[#9E9890] hover:text-red-500 transition-colors" />
                </button>
              </div>

              {/* Pro Tip banner for Home Depot / Lowe's */}
              {receipt.store && (() => {
                const s = receipt.store.toLowerCase()
                if (s.includes('home depot')) return (
                  <div className="mx-4 my-2 flex items-start gap-2 bg-[#F96302]/5 border border-[#F96302]/20 rounded-lg px-3 py-2">
                    <span className="text-[12px]">💡</span>
                    <div>
                      <span className="text-[10px] font-[800] text-[#F96302]">Pro Tip:</span>
                      <span className="text-[10px] font-[600] text-[#1C1A17] ml-1">
                        {isEs ? 'Regístrate en Home Depot Pro Xtra para ahorrar hasta 20% en pedidos al mayor.' : 'Sign up for Home Depot Pro Xtra to save up to 20% on bulk orders.'}
                      </span>
                      <a href="#pro-xtra" className="text-[9px] font-[800] text-[#F96302] ml-1 underline">{isEs ? 'Más info' : 'Learn more'}</a>
                    </div>
                  </div>
                )
                if (s.includes('lowe')) return (
                  <div className="mx-4 my-2 flex items-start gap-2 bg-[#004990]/5 border border-[#004990]/20 rounded-lg px-3 py-2">
                    <span className="text-[12px]">💡</span>
                    <div>
                      <span className="text-[10px] font-[800] text-[#004990]">Pro Tip:</span>
                      <span className="text-[10px] font-[600] text-[#1C1A17] ml-1">
                        {isEs ? "Usa Lowe's for Pros MVPs para descuentos exclusivos y entrega gratis en materiales." : "Use Lowe's for Pros MVPs for exclusive discounts and free delivery on materials."}
                      </span>
                      <a href="#lowes-pro" className="text-[9px] font-[800] text-[#004990] ml-1 underline">{isEs ? 'Más info' : 'Learn more'}</a>
                    </div>
                  </div>
                )
                return null
              })()}

              {/* Receipt items */}
              {receipt.items && receipt.items.length > 0 && (
                <div className="px-4 py-2">
                  <table className="w-full text-[11px]">
                    <tbody>
                      {receipt.items.map((item, i) => {
                        const catConfig = expenseCategories[item.category || 'OTHER'] || DEFAULT_EXPENSE_CATEGORIES.OTHER
                        return (
                          <tr key={i} className="border-t border-[#F2F0EB] first:border-t-0">
                            <td className="py-1.5 font-[600] text-[#1C1A17]">{item.name}</td>
                            <td className="py-1.5">
                              <span className="inline-flex items-center gap-1 text-[9px] font-[700] text-[#9E9890]">
                                <span className="w-[6px] h-[6px] rounded-sm" style={{ background: catConfig.color }} />
                                {isEs ? catConfig.es : catConfig.en}
                              </span>
                            </td>
                            <td className="py-1.5 text-right text-[#9E9890] font-[600]">×{safeNumber(item.quantity)}</td>
                            <td className="py-1.5 text-right text-[#9E9890] font-[600]">${safeMoney(item.unitPrice)}</td>
                            <td className="py-1.5 text-right font-[800]">${safeMoney(item.total)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {(!receipts || receipts.length === 0) && !latestScan && !isLoading && !isReceiptsError && (
        <div className="text-center py-12">
          <Receipt size={36} className="mx-auto text-[#ECEAE4] mb-3" />
          <p className="text-[12px] font-[700] text-[#9E9890]">{isEs ? 'Aún no hay recibos subidos.' : 'No receipts uploaded yet.'}</p>
          <p className="text-[10px] font-[600] text-[#9E9890] mt-1">{isEs ? 'Sube un recibo para rastrear gastos' : 'Upload a receipt to track expenses'}</p>
        </div>
      )}
      {/* === REAL-PROJECT-ONLY SUBTREE END (PR #138C) === */}</>)}
    </div>
  )
}
